import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/services/api/client';
import { cn } from '@/lib/utils';
import { Button } from './button';

/**
 * BOŞ VE HATA DURUMLARI
 * Tasarım dilinin parçasıdır — sonradan eklenmiş uyarı kutuları değil.
 * Her boş durum kullanıcıya bir sonraki aksiyonu gösterir.
 */

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: { label: string; to: string };
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 py-16 text-center',
        className,
      )}
    >
      {icon ? (
        <div className="mb-5 flex size-14 items-center justify-center rounded-pill bg-leaf-50 text-leaf-700">
          {icon}
        </div>
      ) : null}

      <h3 className="text-xl text-ink">{title}</h3>

      {description ? (
        <p className="mt-2 max-w-prose text-base text-ink-muted">{description}</p>
      ) : null}

      {action ? (
        <Button asChild variant="secondary" className="mt-6">
          <Link to={action.to}>{action.label}</Link>
        </Button>
      ) : null}
    </div>
  );
}

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

/** Hata mesajı backend'den gelir; frontend uydurmaz. */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const isNetwork = error instanceof ApiError && error.isNetwork;
  const message =
    error instanceof ApiError
      ? error.message
      : 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.';

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center px-6 py-16 text-center',
        className,
      )}
    >
      <div className="mb-5 flex size-14 items-center justify-center rounded-pill bg-danger-soft text-danger">
        {isNetwork ? (
          <WifiOff className="size-6" aria-hidden />
        ) : (
          <AlertCircle className="size-6" aria-hidden />
        )}
      </div>

      <h3 className="text-xl text-ink">
        {isNetwork ? 'Bağlantı kurulamadı' : 'Bir sorun oluştu'}
      </h3>
      <p className="mt-2 max-w-prose text-base text-ink-muted">{message}</p>

      {onRetry ? (
        <Button variant="secondary" className="mt-6" onClick={onRetry}>
          <RefreshCw className="size-4" aria-hidden />
          Tekrar dene
        </Button>
      ) : null}
    </div>
  );
}

/** Sayfa içi yükleme — tam ekran spinner yerine sakin bir bekleme. */
export function LoadingState({ label = 'Yükleniyor' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-3 px-6 py-16 text-ink-muted"
    >
      <span className="size-2 animate-pulse rounded-pill bg-leaf-400 motion-reduce:animate-none" />
      <span className="text-base">{label}</span>
    </div>
  );
}
